import { Component, computed, DestroyRef, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LayoutService } from '../../../../../../@vex/services/layout.service';
import { fadeInUp400ms } from '../../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../../@vex/animations/stagger.animation';
import {
	Commodity,
	CommodityWithType,
	CostGroup,
	CostGroupCategory,
	CostGroupService,
	CostGroupType,
	CostTemplate,
	CostTemplateService,
	CostTypeMeta,
	GeneralRegulatoryMode,
	InvoiceAmount,
	InvoiceAmountType,
	InvoiceCategory,
	InvoiceCategoryType,
	InvoiceDelivery,
	MetadataService,
	PhysicalUnit,
} from '@eadsysteme/billingclient-v1';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { DateUtil } from '../../../../../util/DateUtil';
import { Icons } from '../../../../../util/Icons';
import { TranslatedCostGroupTemplate } from '../translated-cost-group-template';
import { MatInput } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionService } from '../../../../../services/session.service';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCheckbox } from '@angular/material/checkbox';
import { PeriodService } from '../../period.service';
import { Observable } from 'rxjs';
import { ExpertCostGroupDialogComponent } from './expert-view/expert-cost-group-dialog.component';
import { CostAllocationDetail } from '../../../../../services/data/CostAllocationDetail';
import { CostAllocationHelper } from '../../../../../services/CostAllocationHelper.service';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { BvedCostTypeData } from '../../../../../util/models/bvedCostType/BvedCostType.data';
import { BvedMainCategory, getMainCategoryFromKey, getMainCategoryI18nKey } from '../../../../../util/models/bvedCostType/BvedMainCategory';
import { AllocationDialogComponent } from '../../basic-data/allocations/allocation-dialog/allocation-dialog.component';
import { AllocationOptionComponent } from '../shared/allocation-option/allocation-option.component';
import { DecimalFormatDirective } from '../../../../../directives/decimal-format.directive';

@Component({
	selector: 'vex-cost-group-dialog',
	imports: [
		MatButton,
		MatDialogActions,
		MatDialogContent,
		MatDialogTitle,
		MatFormField,
		MatIcon,
		MatLabel,
		ReactiveFormsModule,
		TranslocoDirective,
		MatSlideToggle,
		MatOption,
		MatSelect,
		MatInput,
		MatTooltip,
		MatCheckbox,
		MatSelectTrigger,
		FormsModule,
		CdkDrag,
		CdkDragHandle,
		AllocationOptionComponent,
		MatIconButton,
		MatSuffix,
		DecimalFormatDirective,
	],
	templateUrl: './cost-group-dialog.component.html',
	styleUrl: './cost-group-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class CostGroupDialogComponent {
	protected layoutService = inject(LayoutService);
	protected dialogRef = inject<MatDialogRef<CostGroupDialogComponent>>(MatDialogRef);
	protected dialogData = inject(MAT_DIALOG_DATA);
	protected dateUtil = inject(DateUtil);
	protected icons = inject(Icons);
	private readonly transloco = inject(TranslocoService);
	private readonly snackBar = inject(SnackbarService);
	private readonly sessionService = inject(SessionService);
	private readonly dialog = inject(MatDialog);
	private readonly formBuilder = inject(FormBuilder);

	private readonly destroyRef = inject(DestroyRef);
	private readonly costAllocationDetailService = inject(CostAllocationHelper);
	private readonly costGroupService = inject(CostGroupService);
	protected readonly selectedPeriodService = inject(PeriodService);
	private readonly costTemplateService = inject(CostTemplateService);
	private readonly metaDataService = inject(MetadataService);

	protected readonly InvoiceCategoryType = InvoiceCategoryType;
	protected readonly CostGroupCategory = CostGroupCategory;

	// CO2-Abrechnung gilt in DE und ohne Regulatorik-Modus (null); nur im österreichischen Modus nicht.
	protected readonly co2Applicable = computed(
		() => this.selectedPeriodService.selectedPeriod()?.generalRegulatoryMode !== GeneralRegulatoryMode.AUSTRIAN_STANDARD
	);

	dialogTitlesByCategory = {
		[CostGroupCategory.HEAT_ENERGY]: {
			add: 'billing.addHeatEnergyCostGroup',
			edit: 'billing.editHeatEnergyCostGroup',
		},
		[CostGroupCategory.ANCILLARY_SERVICES]: {
			add: 'billing.addAncillaryCostGroup',
			edit: 'billing.editAncillaryCostGroup',
		},
		[CostGroupCategory.HEATING_OPERATION]: {
			add: 'billing.addHeatingOpsCostGroup',
			edit: 'billing.editHeatingOpsCostGroup',
		},
		[CostGroupCategory.TENANT_ELECTRICITY]: {
			add: 'billing.addTenantElectricityCostGroup',
			edit: 'billing.editTenantElectricityCostGroup',
		},
	};

	private readonly wageTypes: InvoiceAmountType[] = [InvoiceAmountType.WAGE_35A_1, InvoiceAmountType.WAGE_35A_2, InvoiceAmountType.WAGE_35A_3];

	protected readonly currentCostGroupType: CostGroupType;
	protected currentCommodity: WritableSignal<TranslatedCostGroupTemplate> = signal(undefined);
	protected costGroupTemplates: WritableSignal<TranslatedCostGroupTemplate[]> = signal(undefined);
	protected isShowInStock: WritableSignal<boolean> = signal(false);
	protected isShowCo2: WritableSignal<boolean> = signal(false);
	protected isFeedIn: WritableSignal<boolean> = signal(false);
	protected selectedByTemplate: WritableSignal<boolean> = signal(false);

	protected isInvoicedAmount: WritableSignal<boolean> = signal(true); // true = INVOICED_AMOUNT, false = CREDIT_AMOUNT
	protected isShowCo2Emission: WritableSignal<boolean> = signal(false);
	protected isShowRadioactiveWaste: WritableSignal<boolean> = signal(false);
	protected isShowRenewableEnergyShareEEG: WritableSignal<boolean> = signal(false);
	protected isShowRenewableEnergyShare: WritableSignal<boolean> = signal(false);
	protected isShowNuclearEnergyShare: WritableSignal<boolean> = signal(false);
	protected isShowCoalEnergyShare: WritableSignal<boolean> = signal(false);
	protected isShowGasEnergyShare: WritableSignal<boolean> = signal(false);
	protected isShowOtherFossileEnergyShare: WritableSignal<boolean> = signal(false);

	protected costGroupFg: FormGroup;
	protected currentCostGroup: CostGroup = undefined;
	protected readonly wages = Object.values([InvoiceAmountType.WAGE_35A_1, InvoiceAmountType.WAGE_35A_2, InvoiceAmountType.WAGE_35A_3]);
	private billingId: string = '';
	private billingPeriodId: string = '';
	isHeatEnergyCostGroup: WritableSignal<boolean> = signal(false);
	isTenantElectricityCostGroup: WritableSignal<boolean> = signal(false);

	allocations = signal<CostAllocationDetail[]>([]);
	private initialFormValue: any = null;
	bvedSectionExpanded = false;
	costTypeMeta = Object.entries(CostTypeMeta).map(([key, value]) => ({
		costType: key,
		...value,
	}));

	constructor() {
		const dialogData = this.dialogData;

		this.billingId = dialogData.billingId;
		this.billingPeriodId = dialogData.billingPeriodId;
		this.currentCostGroupType = dialogData.currentCostGroupType;
		this.currentCostGroup = dialogData.currentCostGroup; //only if edit existing costGroup

		this.isHeatEnergyCostGroup.set(this.currentCostGroupType.category === CostGroupCategory.HEAT_ENERGY);
		this.isTenantElectricityCostGroup.set(this.currentCostGroupType.category === CostGroupCategory.TENANT_ELECTRICITY);

		this.costTypeMeta = this.costTypeMeta.filter((value) => value.costGroupCategories.includes(this.currentCostGroupType.category));

		this.costGroupFg = this.initFormGroup();
		this.loadCostGroupTemplates(this.currentCostGroupType.category);

		if (!this.isHeatEnergyCostGroup() && !this.isTenantElectricityCostGroup()) {
			this.enableNames();
		}
		this.loadAllocations();
	}

	private initFormGroup() {
		return this.formBuilder.group({
			template: [{ value: null, disabled: false }, this.isHeatEnergyCostGroup() ? Validators.required : undefined],
			commodity: [{ value: null, disabled: true }, this.isHeatEnergyCostGroup() || this.isTenantElectricityCostGroup() ? Validators.required : undefined],
			name: [{ value: null, disabled: true }, Validators.required],
			printName: [{ value: null, disabled: true }], //Biogas in m³
			description: [{ value: null, disabled: true }],
			unit: [{ value: null, disabled: true }, this.isHeatEnergyCostGroup() || this.isTenantElectricityCostGroup() ? Validators.required : undefined],
			hasInvoiceAmount: [
				{ value: true, disabled: true },
				!this.isHeatEnergyCostGroup() && !this.isTenantElectricityCostGroup() ? Validators.required : undefined,
			],
			hasCo2Amount: [
				{ value: false, disabled: true },
				!this.isHeatEnergyCostGroup() && !this.isTenantElectricityCostGroup() ? Validators.required : undefined,
			],
			selectedWage: [{ value: null, disabled: this.selectedPeriodService.selectedPeriod().locked || this.isEditMode() }],
			selectedAllocation: [{ value: null, disabled: this.selectedPeriodService.selectedPeriod().locked || this.isEditMode() }],
			selectedCostType: [{ value: null, disabled: true }],
			selectedBvedCostType: [{ value: null, disabled: true }],
			preferredVatRate: [{ value: null, disabled: this.selectedPeriodService.selectedPeriod().locked || this.isEditMode() }],
		});
	}

	/**
	 * Holt den bved-Key für einen BvedCostType
	 * @param enumName Der BvedCostType Enum-Name
	 * @returns Der Key im Format "x.y.z" (z.B. "6.2.16")
	 */
	protected getBvedCostTypeKey(enumName: string): string {
		return BvedCostTypeData[enumName]?.key || '';
	}

	/**
	 * Holt die Hauptkategorie für einen BvedCostType
	 * @param enumName Der BvedCostType Enum-Name
	 * @returns Die Hauptkategorie (1-17) oder null
	 */
	protected getBvedMainCategory(enumName: string): BvedMainCategory | null {
		const key = this.getBvedCostTypeKey(enumName);
		return getMainCategoryFromKey(key);
	}

	/**
	 * Gibt den i18n Key für die Hauptkategorie zurück
	 * @param mainCategory Die Hauptkategorie
	 * @returns Der i18n Key für die Übersetzung
	 */
	protected getBvedMainCategoryI18nKey(mainCategory: BvedMainCategory | null): string {
		return getMainCategoryI18nKey(mainCategory);
	}

	private enableNames() {
		if (this.selectedPeriodService.selectedPeriod().locked) {
			return; //if locked, no changes allowed
		}
		this.costGroupFg.get('name').enable();
		this.costGroupFg.get('printName').enable();
		this.costGroupFg.get('description').enable();
	}

	private loadAllocations() {
		this.costAllocationDetailService
			.loadAllocationDetails(this.billingId, this.billingPeriodId, this.currentCostGroupType.category)
			.then((details) => {
				this.allocations.set(details);
				const costAllocationDetail = this.allocations()
					.filter((value) => value.exportId === this.currentCostGroup?.costAllocationExportId)
					?.at(0);
				this.costGroupFg.get('selectedAllocation').setValue(costAllocationDetail);
			})
			.catch(() => this.snackBar.error(this.transloco.translate('errors.loading')));
	}

	private fillExistingCostGroup() {
		const mainDelivery = this.currentCostGroup.invoiceDeliveries.find((o) => o.commodity !== 'CO2');

		// Find the matching commodity/template from the loaded templates
		const matchingTemplate = this.costGroupTemplates()?.find((t) => t.commodity === mainDelivery?.commodity);
		if (matchingTemplate) {
			this.currentCommodity.set(matchingTemplate);
		}
		const matchingCommodity = this.currentCommodity();

		this.costGroupFg.setValue({
			template: null,
			commodity: matchingCommodity ?? '',
			unit: mainDelivery?.physicalUnit ?? PhysicalUnit.UNITS,
			name: this.currentCostGroup.name ?? '',
			printName: this.currentCostGroup.printname ?? '',
			description: this.currentCostGroup.description ?? '',
			hasInvoiceAmount: this.currentCostGroup.invoiceAmounts.map((value) => value.type).includes(InvoiceAmountType.INVOICED_AMOUNT),
			hasCo2Amount:
				this.currentCostGroup.invoiceDeliveries.map((value) => value.commodity).includes(Commodity.CO2) ||
				this.currentCostGroup.invoiceAmounts.map((value) => value.type).includes(InvoiceAmountType.CO2_COST),
			selectedWage: this.currentCostGroup.invoiceAmounts.find((v) => this.wageTypes.includes(v.type))?.type ?? '',
			selectedAllocation: this.allocations().find((v) => v.exportId === this.currentCostGroup.costAllocationExportId) ?? '',
			selectedBvedCostType: this.currentCostGroup.bvedCostType,
			selectedCostType: this.costTypeMeta.find((item) => item.costType === this.currentCostGroup.costType)?.costType ?? null,
			preferredVatRate: this.currentCostGroup.preferredVatRate,
		});

		// this.setNames(commodity.translatedName + ' in ' + (this.costGroupFg.get('unit').value ? this.transloco.translate('enum.' + this.costGroupFg.get('unit').value) : ''));
		this.isShowInStock.set(
			this.currentCostGroup.invoiceCategories.map((value) => value.type).includes(InvoiceCategoryType.INITIAL_STOCK) ||
				this.currentCostGroup.invoiceCategories.map((value) => value.type).includes(InvoiceCategoryType.DELIVERY)
		);
		this.isShowCo2.set(this.currentCostGroup.invoiceDeliveries.map((value) => value.commodity).includes(Commodity.CO2));

		if (this.isTenantElectricityCostGroup()) {
			this.isFeedIn.set(this.currentCostGroup.invoiceCategories.map((value) => value.type).includes(InvoiceCategoryType.FEED_IN));
		}

		if (this.currentCostGroup.invoiceAmounts.map((value) => value.type).includes(InvoiceAmountType.CREDIT_AMOUNT)) {
			this.isInvoicedAmount.set(false);
		} else {
			this.isInvoicedAmount.set(true);
		}

		this.resetEnergyMixCheckboxes();

		for (const delivery of this.currentCostGroup.invoiceDeliveries) {
			this.setTogglesByCommodity(delivery.commodity);
		}

		this.costGroupFg.get('template').disable();
		this.costGroupFg.get('commodity').disable();
		this.costGroupFg.get('unit').disable();
		this.enableNames();

		// Store initial form value for change detection
		this.initialFormValue = JSON.stringify(this.costGroupFg.getRawValue());
	}

	/**
	 * Checks if the form has changed compared to the initial values (edit mode).
	 */
	protected hasChanges(): boolean {
		if (!this.initialFormValue) {
			return true;
		}
		return JSON.stringify(this.costGroupFg.getRawValue()) !== this.initialFormValue;
	}

	private subscribeValueChanges() {
		this.costGroupFg
			.get('commodity')
			.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (data) => {
					if (data) {
						if (!this.selectedByTemplate()) {
							this.clearNames();
						}
						this.enableNames();
						this.costGroupFg.get('template').removeValidators(Validators.required);
					} else {
						this.costGroupFg.get('unit').disable();
					}
				},
			});

		this.costGroupFg
			.get('unit')
			.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (data) => {
					if (data && !this.selectedByTemplate()) {
						this.clearNames();

						const printName = this.costGroupFg.get('commodity')?.value?.translatedName + ' in ' + this.transloco.translate('enum.' + data);
						const splitName: string[] = this.costGroupFg.get('name').value.split(', ');
						splitName[0] = printName;

						this.setNames(printName);
					}
				},
			});

		this.costGroupFg
			.get('template')
			.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (data) => {
					if (data) {
						this.enableNames();

						this.costGroupFg.get('commodity').removeValidators(Validators.required);
						this.costGroupFg.get('unit').removeValidators(Validators.required);
					}
				},
			});
	}

	private setNames(name: string) {
		this.costGroupFg.get('name').setValue(name);
		this.costGroupFg.get('printName').setValue(name);
	}

	private clearNames() {
		this.costGroupFg.get('name').setValue('');
		this.costGroupFg.get('printName').setValue('');
	}

	switchInStock() {
		this.isShowInStock.set(!this.isShowInStock());
	}

	switchCo2() {
		this.isShowCo2.set(!this.isShowCo2());
	}

	switchCo2Emission() {
		this.isShowCo2Emission.set(!this.isShowCo2Emission());
	}

	switchRadioactiveWaste() {
		this.isShowRadioactiveWaste.set(!this.isShowRadioactiveWaste());
	}

	switchRenewableEnergyShareEEG() {
		this.isShowRenewableEnergyShareEEG.set(!this.isShowRenewableEnergyShareEEG());
	}

	switchRenewableEnergyShare() {
		this.isShowRenewableEnergyShare.set(!this.isShowRenewableEnergyShare());
	}

	switchNuclearEnergyShare() {
		this.isShowNuclearEnergyShare.set(!this.isShowNuclearEnergyShare());
	}

	switchCoalEnergyShare() {
		this.isShowCoalEnergyShare.set(!this.isShowCoalEnergyShare());
	}

	switchGasEnergyShare() {
		this.isShowGasEnergyShare.set(!this.isShowGasEnergyShare());
	}

	switchOtherFossileEnergyShare() {
		this.isShowOtherFossileEnergyShare.set(!this.isShowOtherFossileEnergyShare());
	}

	switchInvoiceAmountType() {
		this.isInvoicedAmount.set(!this.isInvoicedAmount());
	}

	switchFeedIn() {
		this.isFeedIn.set(!this.isFeedIn());
	}

	updateSwitchesAndNames(name: string): void {
		const handlers: Record<string, () => void> = {
			template: () => this.handleTemplateSelection(),
			commodity: () => this.handleCommoditySelection(),
			other: () => this.handleOtherSelection(),
			wage: () => this.handleWageSelection(),
			tenant_electricity: () => this.handleTenantElectricitySelection(),
		};

		const handler = handlers[name];
		if (handler) {
			handler();
		}
	}

	private handleTemplateSelection(): void {
		const template = this.costGroupFg.get('template').value as TranslatedCostGroupTemplate;
		this.currentCommodity.set(template);

		this.selectedByTemplate.set(true);
		// In DE/null reagiert CO2 auf die Vorlage; im AT-Modus bleibt CO2 aus (wird nicht mitgesendet).
		this.isShowCo2.set(this.co2Applicable() && this.currentCommodity().hasCo2);
		this.isShowInStock.set(this.currentCommodity().invoiceCategorySet.includes('STOCK'));
		setTimeout(() => {
			this.costGroupFg.get('commodity').setValue(this.currentCommodity());
		}, 0);
		this.costGroupFg.get('selectedBvedCostType').setValue(this.currentCommodity()?.bvedCostType);
		this.costGroupFg.get('selectedCostType').setValue(this.currentCommodity().costType);
		this.costGroupFg.get('unit').setValue(this.currentCommodity().physicalUnit);
		this.setNames(this.currentCommodity()?.translatedName + ' in ' + this.transloco.translate('enum.' + this.currentCommodity().physicalUnit));
	}

	private handleCommoditySelection(): void {
		this.selectedByTemplate.set(false);
		this.costGroupFg.get('template').setValue(null);
		this.costGroupFg.get('template').removeValidators(Validators.required);
		this.costGroupFg.get('selectedBvedCostType').setValue(null);
		this.costGroupFg.get('selectedCostType').setValue(null);
		this.isShowCo2.set(false);
		this.isShowInStock.set(false);

		const selectedCommodity: CommodityWithType = this.costGroupFg.get('commodity').value;
		const selectedUnit: PhysicalUnit = this.costGroupFg.get('unit').value;
		this.setNames(
			this.transloco.translate('commodity.' + selectedCommodity.commodity) +
				' in ' +
				(selectedUnit ? this.transloco.translate('enum.' + selectedUnit) : '')
		);
		if (this.isTenantElectricityCostGroup()) {
			this.isFeedIn.set(false);
		}
	}

	private handleOtherSelection(): void {
		this.selectedByTemplate.set(true);
		const template: TranslatedCostGroupTemplate = this.costGroupFg.get('template').value;
		this.currentCommodity.set(template);

		if (template?.bvedCostType) {
			this.setNames(this.transloco.translate('billing.bvedCostTypes.' + template.bvedCostType));
		} else if (template?.costTemplate) {
			this.setNames(this.transloco.translate('enum.' + template.costTemplate));
		} else if (template?.translatedName) {
			this.setNames(template.translatedName);
		}
		this.costGroupFg.get('selectedBvedCostType').setValue(template.bvedCostType);
		this.costGroupFg.get('selectedCostType').setValue(template.costType);
	}

	private handleWageSelection(): void {
		if (!this.selectedByTemplate()) {
			this.costGroupFg.get('selectedBvedCostType').setValue(null);
			this.costGroupFg.get('selectedCostType').setValue(null);
		}
	}

	private handleTenantElectricitySelection(): void {
		const template: TranslatedCostGroupTemplate = this.costGroupFg.get('template').value;
		this.currentCommodity.set(template);

		this.costGroupFg.get('commodity').setValue(template);
		this.costGroupFg.get('selectedBvedCostType').setValue(template.bvedCostType);
		this.costGroupFg.get('selectedCostType').setValue(template.costType);

		this.costGroupFg.get('unit').setValue(template?.physicalUnit);
		this.isShowInStock.set(template.invoiceCategorySet === 'STOCK');
		this.isFeedIn.set(template?.invoiceCategorySet === 'FEED_IN');
		this.selectedByTemplate.set(true);

		if (template?.costTemplate) {
			this.setNames(this.transloco.translate('enum.' + template.costTemplate));
		}

		this.setInvoiceAmountTypeFromTemplate(template);
		this.resetEnergyMixCheckboxes();
		this.applyAdditionalDeliveries(template);
	}

	private setInvoiceAmountTypeFromTemplate(template: TranslatedCostGroupTemplate): void {
		if (!template?.invoiceAmounts || template.invoiceAmounts.length === 0) {
			this.isInvoicedAmount.set(true);
			return;
		}

		const firstInvoiceAmount = template.invoiceAmounts[0];
		this.isInvoicedAmount.set(firstInvoiceAmount.type !== 'CREDIT_AMOUNT');
	}

	private applyAdditionalDeliveries(template: TranslatedCostGroupTemplate): void {
		if (template?.additionalDeliveries && template.additionalDeliveries.length > 0) {
			for (const delivery of template.additionalDeliveries) {
				this.setTogglesByCommodity(delivery.commodity);
			}
		}
	}

	private resetEnergyMixCheckboxes() {
		this.isShowCo2Emission.set(false);
		this.isShowRadioactiveWaste.set(false);
		this.isShowRenewableEnergyShareEEG.set(false);
		this.isShowRenewableEnergyShare.set(false);
		this.isShowNuclearEnergyShare.set(false);
		this.isShowCoalEnergyShare.set(false);
		this.isShowGasEnergyShare.set(false);
		this.isShowOtherFossileEnergyShare.set(false);
	}

	close(saved: boolean, savedCostGroup?: CostGroup): void {
		if (saved && !savedCostGroup) {
			this.createOrUpdateCostGroup(saved);
		} else {
			this.dialogRef.close({ saved, data: savedCostGroup });
		}
	}

	private createOrUpdateCostGroup(saved: boolean) {
		let costGroupToSave: CostGroup;
		if (this.currentCostGroupType.category === CostGroupCategory.HEAT_ENERGY) {
			costGroupToSave = this.createHeatingCostGroup();
		} else if (
			this.currentCostGroupType.category === CostGroupCategory.ANCILLARY_SERVICES ||
			this.currentCostGroupType.category === CostGroupCategory.HEATING_OPERATION
		) {
			costGroupToSave = this.createAncillaryOpsCostGroup();
		} else if (this.currentCostGroupType.category === CostGroupCategory.TENANT_ELECTRICITY) {
			costGroupToSave = this.createTenantElectricityCostGroup();
		}

		if (this.currentCostGroup?.exportId) {
			costGroupToSave.exportId = this.currentCostGroup?.exportId;
			this.costGroupService
				.updateCostGroup(this.sessionService.effectiveArea, this.billingId, this.currentCostGroup?.exportId, costGroupToSave)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (newCostGroup) => {
						this.snackBar.success(this.transloco.translate('billing.updatedCostGroup') + ': ' + newCostGroup.name);
						this.dialogRef.close({ saved, data: newCostGroup });
					},
					error: (err) => {
						this.snackBar.error(this.transloco.translate('errors.updatingBillingCostGroup'));
					},
				});
		} else {
			this.costGroupService
				.createCostGroup(this.sessionService.effectiveArea, this.billingId, this.billingPeriodId, costGroupToSave)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (newCostGroup) => {
						this.snackBar.success(this.transloco.translate('billing.newCostGroupCreated') + ': ' + newCostGroup.name);
						this.dialogRef.close({ saved, data: newCostGroup });
					},
					error: (data) => {
						this.snackBar.error(this.transloco.translate('billing.newCostGroupFailed'));
					},
				});
		}
	}

	private createHeatingCostGroup(): CostGroup {
		// if costGroup is edited, only names can change
		if (this.isEditMode()) {
			return this.updateCurrentCustGroupByFormValues();
		}

		const invoiceDeliveriesToSave: InvoiceDelivery[] = [
			{
				physicalUnit: this.costGroupFg.get('unit').value,
				commodity: this.costGroupFg.get('commodity').value.commodity,
			},
		];

		// CO2-Lieferung und CO2-Betragsart hängen am CO2-Flag: nicht CO2-behaftet → keine von beiden senden.
		if (this.isShowCo2()) {
			const co2TemplateDelivery = this.currentCommodity()?.additionalDeliveries?.find((d) => d.commodity === Commodity.CO2);
			invoiceDeliveriesToSave.push({
				physicalUnit: co2TemplateDelivery?.physicalUnit ?? PhysicalUnit.KG,
				commodity: Commodity.CO2,
			});
		}

		const invoiceCategoriesToSave: InvoiceCategory[] = [];
		const invoiceAmountsToSave: InvoiceAmount[] = [];
		invoiceAmountsToSave.push({ type: InvoiceAmountType.INVOICED_AMOUNT });
		if (this.isShowCo2()) {
			invoiceAmountsToSave.push({ type: InvoiceAmountType.CO2_COST });
		}
		// Lager-Toggle steuert nur die Rechnungskontexte (Lager-Kategorien vs. Verbrauch).
		if (this.isShowInStock()) {
			invoiceCategoriesToSave.push({ type: InvoiceCategoryType.INITIAL_STOCK });
			invoiceCategoriesToSave.push({ type: InvoiceCategoryType.DELIVERY });
			invoiceCategoriesToSave.push({ type: InvoiceCategoryType.DEDUCTION });
			invoiceCategoriesToSave.push({ type: InvoiceCategoryType.REMAINING_STOCK });
		} else {
			invoiceCategoriesToSave.push({ type: InvoiceCategoryType.CONSUMPTION });
		}

		const costGroupToSave: CostGroup = {
			name: this.costGroupFg.get('name').value ?? '',
			description: this.costGroupFg.get('description').value ?? '',
			printname: this.costGroupFg.get('printName').value ?? '',
			invoiceDeliveries: invoiceDeliveriesToSave,
			invoiceAmounts: invoiceAmountsToSave,
			invoiceCategories: invoiceCategoriesToSave,

			costGroupTypeExportId: this.currentCostGroupType.exportId,
		};

		costGroupToSave.costAllocationExportId = this.costGroupFg.get('selectedAllocation').value?.exportId;
		costGroupToSave.bvedCostType = this.costGroupFg.get('selectedBvedCostType').value;
		costGroupToSave.costType = this.costGroupFg.get('selectedCostType').value;
		costGroupToSave.preferredVatRate = this.costGroupFg.get('preferredVatRate').value;
		return costGroupToSave;
	}

	private createTenantElectricityCostGroup(): CostGroup {
		// if costGroup is edited, only names can change
		if (this.isEditMode()) {
			return this.updateCurrentCustGroupByFormValues();
		}

		const invoiceDeliveriesToSave: InvoiceDelivery[] = [
			{
				physicalUnit: this.costGroupFg.get('unit').value,
				commodity: this.costGroupFg.get('commodity').value.commodity,
			},
		];

		if (this.isShowCo2Emission()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.G_TO_KWH,
				commodity: Commodity.CO2_EMISSION,
			});
		}
		if (this.isShowRadioactiveWaste()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.G_TO_KWH,
				commodity: Commodity.RADIOACTIVE_WASTE,
			});
		}
		if (this.isShowRenewableEnergyShareEEG()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.PERCENT,
				commodity: Commodity.RENEWABLE_ENERGY_SHARE_EEG,
			});
		}
		if (this.isShowRenewableEnergyShare()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.PERCENT,
				commodity: Commodity.RENEWABLE_ENERGY_SHARE,
			});
		}
		if (this.isShowNuclearEnergyShare()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.PERCENT,
				commodity: Commodity.NUCLEAR_ENERGY_SHARE,
			});
		}
		if (this.isShowCoalEnergyShare()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.PERCENT,
				commodity: Commodity.COAL_ENERGY_SHARE,
			});
		}
		if (this.isShowGasEnergyShare()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.PERCENT,
				commodity: Commodity.GAS_ENERGY_SHARE,
			});
		}
		if (this.isShowOtherFossileEnergyShare()) {
			invoiceDeliveriesToSave.push({
				physicalUnit: PhysicalUnit.PERCENT,
				commodity: Commodity.OTHER_FOSSILE_ENERGY_SHARE,
			});
		}

		const invoiceCategoriesToSave: InvoiceCategory[] = [];
		const invoiceAmountsToSave: InvoiceAmount[] = [];
		if (this.isInvoicedAmount()) {
			invoiceAmountsToSave.push({ type: InvoiceAmountType.INVOICED_AMOUNT });
		} else {
			invoiceAmountsToSave.push({ type: InvoiceAmountType.CREDIT_AMOUNT });
		}

		if (this.isFeedIn()) {
			invoiceCategoriesToSave.push({ type: InvoiceCategoryType.FEED_IN });
		} else {
			invoiceCategoriesToSave.push({ type: InvoiceCategoryType.CONSUMPTION });
		}

		const costGroupToSave: CostGroup = {
			name: this.costGroupFg.get('name').value ?? '',
			description: this.costGroupFg.get('description').value ?? '',
			printname: this.costGroupFg.get('printName').value ?? '',
			invoiceDeliveries: invoiceDeliveriesToSave,
			invoiceAmounts: invoiceAmountsToSave,
			invoiceCategories: invoiceCategoriesToSave,

			costGroupTypeExportId: this.currentCostGroupType.exportId,
		};

		costGroupToSave.costAllocationExportId = this.costGroupFg.get('selectedAllocation').value?.exportId;
		costGroupToSave.bvedCostType = this.costGroupFg.get('selectedBvedCostType').value;
		costGroupToSave.costType = this.costGroupFg.get('selectedCostType').value;
		costGroupToSave.preferredVatRate = this.costGroupFg.get('preferredVatRate').value;
		return costGroupToSave;
	}

	getTitleKey(): string {
		const category = this.currentCostGroupType.category;
		return this.currentCostGroup ? this.dialogTitlesByCategory[category].edit : this.dialogTitlesByCategory[category].add;
	}

	/**
	 * Checks if a CostGroup is currently being edited.
	 * @returns {boolean} True if currentCostGroup has a truthy ID.
	 */
	protected isEditMode(): boolean {
		return !!this.currentCostGroup?.exportId;
	}

	private createAncillaryOpsCostGroup(): CostGroup {
		// if costGroup is edited, only names can change
		if (this.isEditMode()) {
			return this.updateCurrentCustGroupByFormValues();
		}

		const invoiceCategoriesToSave: InvoiceCategory[] = [];
		const invoiceAmountsToSave: InvoiceAmount[] = [];
		const invoiceDeliveriesToSave: InvoiceDelivery[] = [];
		invoiceAmountsToSave.push({ type: InvoiceAmountType.INVOICED_AMOUNT });
		if (this.costGroupFg.get('hasCo2Amount').value) {
			invoiceAmountsToSave.push({ type: InvoiceAmountType.CO2_COST });
		}
		if (this.costGroupFg.get('selectedWage')?.value) {
			invoiceAmountsToSave.push({ type: this.costGroupFg.get('selectedWage')?.value });
		}

		invoiceCategoriesToSave.push({ type: InvoiceCategoryType.BILL });

		const costGroupToSave: CostGroup = {
			name: this.costGroupFg.get('name').value ?? '',
			description: this.costGroupFg.get('description').value ?? '',
			printname: this.costGroupFg.get('printName').value ?? '',
			invoiceDeliveries: invoiceDeliveriesToSave,
			invoiceAmounts: invoiceAmountsToSave,
			invoiceCategories: invoiceCategoriesToSave,

			costGroupTypeExportId: this.currentCostGroupType.exportId,
		};

		costGroupToSave.costAllocationExportId = this.costGroupFg.get('selectedAllocation').value?.exportId;
		costGroupToSave.bvedCostType = this.costGroupFg.get('selectedBvedCostType').value;
		costGroupToSave.costType = this.costGroupFg.get('selectedCostType')?.value;
		costGroupToSave.preferredVatRate = this.costGroupFg.get('preferredVatRate')?.value;
		return costGroupToSave;
	}

	private updateCurrentCustGroupByFormValues(): CostGroup {
		// if cost groups is edited. only names can change
		this.currentCostGroup.name = this.costGroupFg.get('name').value ?? '';
		this.currentCostGroup.description = this.costGroupFg.get('description').value ?? '';
		this.currentCostGroup.printname = this.costGroupFg.get('printName').value ?? '';
		this.currentCostGroup.costAllocationExportId = this.costGroupFg.get('selectedAllocation').value?.exportId;
		this.currentCostGroup.bvedCostType = this.costGroupFg.get('selectedBvedCostType').value;
		this.currentCostGroup.costType = this.costGroupFg.get('selectedCostType')?.value;
		this.currentCostGroup.preferredVatRate = this.costGroupFg.get('preferredVatRate')?.value;
		return this.currentCostGroup;
	}

	openExpertView() {
		this.updateCurrentCustGroupByFormValues();

		const matDialogRef = this.dialog.open(ExpertCostGroupDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			data: {
				currentCostGroup: this.currentCostGroup,
				currentTemplate: this.currentCommodity(),
				billingId: this.billingId,
				billingPeriodId: this.billingPeriodId,
				allocations: this.allocations(),
				costTypes: this.costTypeMeta,
				bvedCostTypes: [this.currentCommodity()?.bvedCostType],
			},
		});
		matDialogRef.afterClosed().subscribe((result) => {
			if (result?.saved) {
				//successfully saved in expert mode, close this dialog and pass over the updated cost group
				this.close(true, result?.data);
			}
		});
	}

	private loadCostGroupTemplates(category: CostGroupCategory) {
		let loadTemplates$: Observable<CostTemplate[]>;
		switch (category) {
			case 'ANCILLARY_SERVICES':
				loadTemplates$ = this.costTemplateService.getAncillaryServicesCostTemplate(
					this.sessionService.effectiveArea,
					this.billingId,
					this.billingPeriodId
				);
				break;
			case 'HEAT_ENERGY':
				loadTemplates$ = this.costTemplateService.getHeatEnergyCostTemplate(this.sessionService.effectiveArea, this.billingId, this.billingPeriodId);
				break;
			case 'HEATING_OPERATION':
				loadTemplates$ = this.costTemplateService.getHeatingOperationCostTemplate(
					this.sessionService.effectiveArea,
					this.billingId,
					this.billingPeriodId
				);
				break;
			case 'TENANT_ELECTRICITY':
				loadTemplates$ = this.costTemplateService.getTenantElectricityCostTemplate(
					this.sessionService.effectiveArea,
					this.billingId,
					this.billingPeriodId
				);
				break;
			default:
				return null;
		}
		if (loadTemplates$) {
			loadTemplates$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
				next: (data) => {
					this.costGroupTemplates.set(this.fillTranslatedCostGroups(data));

					if (this.currentCostGroup) {
						//isEdit
						this.fillExistingCostGroup();
					} else {
						this.subscribeValueChanges();
					}
				},
			});
		}
	}

	private fillTranslatedCostGroups(data: CostTemplate[]): TranslatedCostGroupTemplate[] {
		const translatedCostGroups: TranslatedCostGroupTemplate[] = [];

		for (const item of data) {
			const costGroup: TranslatedCostGroupTemplate = item;
			costGroup.translatedName = this.transloco.translate('commodity.' + item.commodity);
			if (item.additionalDeliveries?.length > 0) {
				costGroup.hasCo2 = item.additionalDeliveries.some((delivery) => delivery.commodity === 'CO2');
			}
			translatedCostGroups.push(costGroup);
		}
		translatedCostGroups.sort((a, b) => a?.translatedName?.localeCompare(b?.translatedName));
		return translatedCostGroups;
	}

	protected getCommodityName() {
		if (this.currentCommodity()) {
			return this.currentCommodity()?.translatedName ?? 'nameByTemplate';
		} else {
			return this.costGroupFg.get('commodity')?.value?.translatedName ?? 'NameByCommodity';
		}
	}

	protected getAllocationName() {
		return this.costAllocationDetailService.getAllocationDisplayName(this.costGroupFg.get('selectedAllocation')?.value);
	}

	private setTogglesByCommodity(commodity: Commodity) {
		switch (commodity) {
			case 'CO2_EMISSION':
				this.isShowCo2Emission.set(true);
				break;
			case 'RADIOACTIVE_WASTE':
				this.isShowRadioactiveWaste.set(true);
				break;
			case 'RENEWABLE_ENERGY_SHARE_EEG':
				this.isShowRenewableEnergyShareEEG.set(true);
				break;
			case 'RENEWABLE_ENERGY_SHARE':
				this.isShowRenewableEnergyShare.set(true);
				break;
			case 'NUCLEAR_ENERGY_SHARE':
				this.isShowNuclearEnergyShare.set(true);
				break;
			case 'COAL_ENERGY_SHARE':
				this.isShowCoalEnergyShare.set(true);
				break;
			case 'GAS_ENERGY_SHARE':
				this.isShowGasEnergyShare.set(true);
				break;
			case 'OTHER_FOSSILE_ENERGY_SHARE':
				this.isShowOtherFossileEnergyShare.set(true);
				break;
		}
	}

	/**
	 * Opens the AllocationDialogComponent to create a new allocation.
	 * After successful creation, the new allocation is added to the list and automatically selected.
	 */
	protected openAddAllocationDialog() {
		const matDialogRef = this.dialog.open(AllocationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			data: {
				costAllocationIdToEdit: undefined,
				costGroup: undefined,
				billingId: this.billingId,
				billingPeriod: this.selectedPeriodService.selectedPeriod(),
				costGroupCategory: this.currentCostGroupType.category,
			},
		});
		matDialogRef.afterClosed().subscribe({
			next: async (result) => {
				if (result) {
					// Reload allocations and select the new one
					this.costAllocationDetailService
						.loadAllocationDetails(this.billingId, this.billingPeriodId, this.currentCostGroupType.category)
						.then((enriched) => {
							this.allocations.set(enriched);

							// Find and select the newly created allocation
							const newAllocation = enriched.find((a) => a.exportId === result.exportId);
							if (newAllocation) {
								this.costGroupFg.get('selectedAllocation').setValue(newAllocation);
								this.snackBar.success(this.transloco.translate('billing.allocationCreatedAndSelected'));
							}
						})
						.catch(() => this.snackBar.error(this.transloco.translate('errors.loading')));
				}
			},
		});
	}
}
